import { DOCUMENT } from '@angular/common';
import {
	ApplicationRef,
	createComponent,
	effect,
	EmbeddedViewRef,
	inject,
	Injectable,
	RendererFactory2,
	untracked,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ReachModalsService } from '~app-client/modals/services';
import { CommonInfoModalKey, CommonInfoModalsService } from '~ng-common-lib/modals/modals.service';

import { InfoModalBaseComponent } from '../components/public_api';
import { InfoModalKey } from '../types/public_api';
import { keyToData } from '../utils/public_api';

export const commonToKeys: Record<CommonInfoModalKey, InfoModalKey> = {
	[CommonInfoModalKey.CHAT_AI_HOW_IT_WORKS]: InfoModalKey.CHAT_AI_HOW_IT_WORKS,
};

@Injectable({ providedIn: 'root' })
export class InfoModalsService {
	private readonly commonInfoModalsService = inject(CommonInfoModalsService);
	private readonly document = inject(DOCUMENT);
	private readonly modalsService = inject(ReachModalsService);
	private readonly appRef = inject(ApplicationRef);
	private readonly rendererFactory = inject(RendererFactory2);

	private renderer = this.rendererFactory.createRenderer(null, null);

	constructor() {
		effect(() => {
			const modal = this.commonInfoModalsService.$modalToOpen();
			untracked(() => {
				const key = modal ? commonToKeys[modal] : null;
				if (key) {
					void this.openModal(key);
				}
			});
		});
	}

	public async openModal(key: InfoModalKey, zIndex?: number): Promise<boolean> {
		const data = keyToData[key];
		const modalComponentRef = createComponent(InfoModalBaseComponent, {
			environmentInjector: this.appRef.injector,
		});

		modalComponentRef.instance.hostZIndex = zIndex;
		modalComponentRef.instance.infoModalKey = key;
		modalComponentRef.instance.i18nName = data.i18nName;
		modalComponentRef.instance.slides = data.slides;

		this.appRef.attachView(modalComponentRef.hostView);

		const modalDomElement = (modalComponentRef.hostView as EmbeddedViewRef<HTMLElement>)
			.rootNodes[0] as HTMLElement;

		this.renderer.appendChild(this.document.body, modalDomElement);

		const res = await firstValueFrom(this.modalsService.modalClosed$);

		modalDomElement.remove();

		return res;
	}
}
